import { isProduction } from '@upright/common/environment';
import { EditVersionPayload, GetModelVersionsResponse } from '@upright/common/model/versions/api-types';
import { ModelVersion, SnapshotType } from '@upright/common/model/versions/types';
import { SuccessOrError } from '@upright/common/types-api';
import { useIsTeam } from '@upright/ui/common/generic/hooks';
import { useMutation, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { fetchOrThrow } from '../../common/fetchers/fetch-or-throw';

type Select<T> = (response: GetModelVersionsResponse) => T;

const FETCH_VERSIONS = 'getVersions';

export function useGetVersions<T = GetModelVersionsResponse>(select?: Select<T>) {
  const isTeam = useIsTeam();
  return useQuery({
    queryKey: [FETCH_VERSIONS, { isTeam }],
    queryFn: getVersions,
    select,
    enabled: isTeam,
  });
}

export function useGetAvailableVersions(select?: undefined): UseQueryResult<GetModelVersionsResponse, Error>;
export function useGetAvailableVersions<T = GetModelVersionsResponse>(select: Select<T>): UseQueryResult<T, Error>;
export function useGetAvailableVersions<T = GetModelVersionsResponse>(select?: Select<T>) {
  return useGetVersions(versions => {
    const availableVersions = versions.filter(isVersionAvailable);
    return select ? select(availableVersions) : availableVersions;
  });
}

export async function getVersions() {
  return await fetchOrThrow<GetModelVersionsResponse>('/versions', 'GET');
}

function isVersionAvailable(version: ModelVersion) {
  return version.type !== SnapshotType.DELETED;
}

/**
 * Detects whether a concrete model version is unavailable in the current
 * environment. Outside production the database is a stripped snapshot that only
 * holds a subset of versions, so a company / result can resolve to a version
 * whose data was never loaded here — every version-scoped fetch for it then
 * 4xx/5xxs (e.g. contributing products, size factors, product set), which is the
 * source of the console-error pileup on such pages. In production every version
 * is present, so this is always `false` there.
 *
 * Returns `false` while the version list is still loading or unknown (including
 * for non-team users, who can't read the version list) so callers fall back to
 * their normal behavior rather than hiding data or skipping fetches on a false
 * negative.
 */
export function useModelVersionAvailability(version: string | undefined): {
  isUnavailableInThisEnvironment: boolean;
} {
  const availableVersionsQuery = useGetAvailableVersions();

  if (isProduction() || version === undefined || !availableVersionsQuery.isSuccess) {
    return { isUnavailableInThisEnvironment: false };
  }
  const isAvailable = availableVersionsQuery.data.some(v => v.number === version);
  return { isUnavailableInThisEnvironment: !isAvailable };
}

async function deleteVersion(version: string) {
  return await fetchOrThrow<SuccessOrError>(`/version/${version}`, 'DELETE');
}

async function downgradeVersion(version: string) {
  return await fetchOrThrow<SuccessOrError>(`/version/${version}/downgrade`, 'PATCH');
}

async function editVersion(version: string, payload: EditVersionPayload) {
  return await fetchOrThrow<SuccessOrError>(`/version/${version}`, 'PATCH', payload);
}

export function useDeleteVersion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (version: string) => {
      return await deleteVersion(version);
    },

    onSettled: () => {
      void queryClient.refetchQueries({
        queryKey: [FETCH_VERSIONS],
      });
    },

    onError: error => {
      alert(error.message);
    },
  });
}

export function useEditVersion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: EditVersionPayload & { version: string }) => {
      const { version, ...payload } = params;
      return await editVersion(version, payload);
    },

    onSettled: () => {
      void queryClient.refetchQueries({
        queryKey: [FETCH_VERSIONS],
      });
    },
  });
}

export function useDowngradeVersion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (version: string) => {
      return await downgradeVersion(version);
    },

    onSettled: () => {
      void queryClient.refetchQueries({
        queryKey: [FETCH_VERSIONS],
      });
    },

    onError: error => {
      alert(error.message);
    },
  });
}
